import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, ModalSettings, WizardMarkableActions } from 'mbs-ui-kit';
import { MbsPopupType } from 'mbs-ui-kit/utils/enums/mbs-popup-enum';
import { FormsUtil } from 'mbs-ui-kit/utils/forms-util';
import { AzureAccessTierType, StorageClass, StorageType } from 'mbs-ui-shared@models';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { BehaviorSubject, OperatorFunction } from 'rxjs';
import { DataForPath } from '../helpers/bases/base-for-steps-helper';
import { RemoteManagementWizardsService } from '../services/remote-management-wizards.service';
import { EnableStepModalComponent } from './components/enable-step-modal/enable-step-modal.component';
import { LegacyPropertyDisablingModalComponent } from './components/legacy-property-disabling-modal.component';

export enum AgentVersions {
  AzureCold = '8.0.0',
  BlockCount = '7.1.1',
  EFS = '7.6',
  FFI = '7.8',
  GlacierIR = '7.3.1',
  GlacierIRLinux = '3.3.1'
}

export type StepLoadInfo = { loading: boolean; isHyperV?: boolean };

export type FormPipeOperators = [
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?
];

export enum AfterEnabledStepActionType {
  next = 0,
  back = 1,
  save = 2
}

const IBBEnableOtherHidden: { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } = {
  [PlanTypes.BackupDiskImagePlan]: 'Enabled',
  [PlanTypes.Plan]: 'Hidden',
  [PlanTypes.DataBasePlan]: 'Hidden',
  [PlanTypes.ExchangePlan]: 'Hidden',
  [PlanTypes.BackupVMware]: 'Hidden',
  [PlanTypes.BackupHyperV]: 'Hidden'
};

@UntilDestroy()
@Directive()
export abstract class StepBase<T extends { valid?: boolean }> implements ControlValueAccessor {
  @Input() title: string;
  @Input() planTypeText = 'Default';
  @Input() stepFocused = false;
  @Input() helpLink: string;

  @Output() loadInfo = new EventEmitter<StepLoadInfo>();
  @Output() isValid = new EventEmitter<boolean>();
  @Output() registerRequiredStep = new EventEmitter<string>();
  @Output() unregisterRequiredStep = new EventEmitter<string>();
  @Output() nextStep = new EventEmitter<boolean>();
  @Output() backStep = new EventEmitter();
  @Output() addStepMark = new EventEmitter<{ stepName: string; actions: WizardMarkableActions }>();
  @Output() removeStepMark = new EventEmitter<{ stepName: string; actions: Array<keyof WizardMarkableActions> }>();

  public readonly mbsPopupType = MbsPopupType;

  public syntheticFullVisible: 'Enabled' | 'Disabled' | 'Hidden' = null;
  public importedPlanChanged = false;
  public stepForm: UntypedFormGroup;
  public regExpForInputAfter = /\\[ ]/;
  public regExpForInputBefore = /[ ]\\/;
  public splitForInputBefore = ' \\';
  public splitForInput = '\\ ';
  public joinForInput = '\\';

  protected initialSyntheticFull: boolean;
  protected userInteractWithSteps: boolean;
  protected confirmedAsEnabled = false;
  protected legacyUnsupportedProperties: Array<string> = [];
  protected syntheticFullSettingPossible = new BehaviorSubject<boolean>(null);
  protected selectedStorageType: StorageType = null;
  /**
   * [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]: 'Enabled' | 'Disabled' | 'Hidden'
   * [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]: { [PlanTypes]: 'Enabled' | 'Disabled' | 'Hidden' }
   * https://docs.google.com/spreadsheets/d/1D0XRduqatjPsGXFKKk92bOZ9Pq9wgTzQmo_1oHCOQ-c/
   * @private
   */
  protected syntheticFullVisibleMap: {
    windows: { [key: string]: 'Enabled' | 'Disabled' | 'Hidden' | { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } };
    unix: { [key: string]: 'Enabled' | 'Disabled' | 'Hidden' | { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } };
  } = {
    windows: {
      [`${StorageType.Azure}-${AzureAccessTierType.Hot}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Cool}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Cold}-${null}-${true}`]: 'Disabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Archive}-${null}-${true}`]: 'Disabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Hot}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Cool}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Cold}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Archive}-${null}-${false}`]: 'Hidden',
      [`${StorageType.BackblazeB2}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.BackblazeB2}-${null}-${null}-${false}`]: 'Hidden',
      [`${StorageType.GoogleCloud}-${null}-${null}-${true}`]: 'Hidden',
      [`${StorageType.GoogleCloud}-${null}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.Minio}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${true}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.INTELLIGENT_TIERING}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.ONEZONE_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.INTELLIGENT_TIERING}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.ONEZONE_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER}-${null}-${false}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER_IR}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER_IR}-${null}-${false}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.DEEP_ARCHIVE}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.DEEP_ARCHIVE}-${null}-${false}`]: 'Disabled',
      [`${StorageType.UbAmazonS3}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.UbAmazonS3}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.S3Compatible}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.S3Compatible}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.S3Compatible}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.S3Compatible}-${null}-${true}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.Wasabi}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Wasabi}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.CblWasabi}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.CblWasabi}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      Other: 'Hidden'
    },
    unix: {
      All: 'Hidden'
    }
  };

  private myValue: T = null;
  private get state() {
    return RemoteManagementWizardsService.getWizardState(this.mainService);
  }

  get planType(): PlanTypes {
    return this.mainService.type;
  }

  get isNBF(): boolean {
    return this.mainService.isNBF;
  }

  get isOffline(): boolean {
    return !this.isRDMode && this.mainService.isOffline;
  }

  get isLinux(): boolean {
    return this.mainService.isLinux;
  }

  get isMac(): boolean {
    return this.mainService.isMac;
  }

  get isUnix(): boolean {
    return this.mainService.isUnix;
  }

  get isRDMode(): boolean {
    return this.state.isRDMode;
  }

  get isCreate(): boolean {
    return this.mainService.mode === PlanMode.create;
  }

  get isEdit(): boolean {
    return this.mainService.mode === PlanMode.edit;
  }

  get value(): T {
    return this.myValue;
  }

  set value(value: T) {
    this.myValue = value;
    this.notifyValueChange();
  }

  get isHyperV(): boolean {
    return this.state.isHyperV;
  }

  get isVMWare(): boolean {
    return this.state.isVMWare;
  }

  get isRestore(): boolean {
    return this.state.isRestore;
  }

  get isRestoreDB(): boolean {
    return this.isRestoreFileDatabase || this.isRestoreDatabase;
  }

  get isRestoreFileDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDatabaseFilesPlan;
  }

  get isRestoreDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDataBasePlan;
  }

  get isRestorePlan(): boolean {
    return this.planType === PlanTypes.RestorePlan;
  }

  get isRestoreIbb(): boolean {
    return this.planType === PlanTypes.RestoreDiskImagePlan;
  }

  get isRestoreVMWareToVSphere(): boolean {
    return this.planType === PlanTypes.RestoreVMware;
  }

  get isRestoreVMWare(): boolean {
    return this.isRestoreVMWareToVSphere || this.planType === PlanTypes.RestoreVMwareToCustom;
  }

  get isRestoreHyperV(): boolean {
    return this.planType === PlanTypes.RestoreHyperV || this.planType === PlanTypes.RestoreHyperVToCustom;
  }

  get isVMRestore(): boolean {
    return this.isRestoreVMWare || this.isRestoreHyperV;
  }

  get isIBBPlan(): boolean {
    return this.planType === PlanTypes.BackupDiskImagePlan;
  }

  get isBackupPlan(): boolean {
    return this.planType === PlanTypes.Plan;
  }

  get isSQLPlan(): boolean {
    return this.state.isSQLPlan;
  }

  get canShowFFI(): boolean {
    if (this.isUnix || this.isFFIEnabled || this.isRestore || !this.isNBF) {
      return false;
    }

    return true;
  }

  get isFFIEnabled(): boolean {
    if (this.isUnix || this.isRestore || this.isSQLPlan) {
      return false;
    }

    const rightVersion = this.agentHigherOrEqualThat(AgentVersions.FFI);
    return (this.isRDMode && this.isNBF) || (this.isNBF && rightVersion);
  }

  get isAppAwareEnabled(): boolean {
    return this.state.isAppAwareEnabled;
  }

  protected constructor(public mainService: RemoteManagementWizardsService) {}

  /**
   * Method for mass change state
   * Also can consider legacyUnsupportedProperties property
   * @param {String[]} controlNames
   * @param {Boolean} isEnabled
   * @protected
   */
  protected toggleFormControls(controlNames: string[], isEnabled: boolean): void {
    let controls = controlNames;
    const actionName = isEnabled ? 'enable' : 'disable';

    if (isEnabled && !this.isNBF) {
      controls = controls.filter((name) => !this.legacyUnsupportedProperties.includes(name));
    }

    controls.forEach((name: string) => this.stepForm.get(name)?.[actionName]?.());
  }

  abstract initForm(): void;

  protected agentHigherThat(version: AgentVersions): boolean {
    const v = version.replace(/[.]/g, '');

    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, v.length) > +v;
  }

  protected agentHigherOrEqualThat(version: AgentVersions): boolean {
    const v = version.replace(/[.]/g, '');

    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, v.length) >= +v;
  }

  /**
   * Don`t forget to use <mbs-wizards-legacy-property-tooltip *ngIf="stepForm.get(...).disabled"></mbs-wizards-legacy-property-tooltip>
   * @param {Boolean} force
   * @protected
   * @see legacyUnsupportedProperties
   * @see getLegacyPropertyChangeHandler
   */
  protected disableLegacyUnsupportedProperties(force = false): void {
    if (this.isNBF) {
      return;
    }

    this.legacyUnsupportedProperties.forEach((key) => {
      if (!this.stepForm.get(key).value || force) {
        this.stepForm.get(key).disable();
      }
    });
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this)];
  }

  protected initFormEvents(): void {
    this.stepForm.valueChanges.pipe(...this.getPipeOperators()).subscribe({ next: (value: T) => this.onStepFormChange(value) });
  }

  onStepFormChange(value: T): void {
    this.value = { ...value, valid: this.stepForm.valid };
  }

  updateForm(value: T): void {
    this.stepForm.reset(value);
  }

  forceValid(data: any = null): void {
    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  resetValidStateForValidFields(): void {
    if (this.stepForm) {
      const value = this.stepForm.getRawValue();
      for (const key in value) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue;

        const ctrl = this.stepForm.get(key) as FormControl;
        if (ctrl.valid) ctrl.reset(value[key]);
      }
    }
  }

  setValues(): void {
    if (this.isUnix) {
      this.regExpForInputAfter = /\/[ ]/;
      this.regExpForInputBefore = /[ ]\//;
      this.splitForInput = '/ ';
      this.splitForInputBefore = ' /';
      this.joinForInput = '/';
    }
  }

  getValueForOperationsWithPath(): DataForPath {
    return {
      isUnix: this.isUnix,
      regExpAfter: this.regExpForInputAfter,
      regExpBefore: this.regExpForInputBefore,
      split: this.splitForInput,
      join: this.joinForInput,
      splitForBefore: this.splitForInputBefore
    };
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(value: T): void {
    this.myValue = value;
    if (value) this.updateForm(value);
  }

  onChange: (value) => {};
  onTouched: () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected showEnableStepModal(
    modalService: ModalService,
    settings: ModalSettings = { collapsing: true },
    type: AfterEnabledStepActionType = AfterEnabledStepActionType.next
  ): void {
    const isSave = type === AfterEnabledStepActionType.save;
    const actionName = type === AfterEnabledStepActionType.back ? 'backStep' : 'nextStep';

    modalService
      .openCustom(EnableStepModalComponent, settings)
      .then((confirm) => {
        if (!confirm) return void (isSave ? this[actionName].emit(isSave) : this[actionName].emit());

        this.confirmedAsEnabled = true;
      })
      .catch(() => (isSave ? this[actionName].emit(isSave) : this[actionName].emit()));
  }

  getLegacyPropertyChangeHandler(propertyName: string, propertyTitle: string, modalService: ModalService): (newValue: boolean) => void {
    return (newValue: boolean) => {
      if (!this.isNBF && !newValue) {
        this.askDisablingLegacyProperty(propertyTitle, modalService).then(
          (confirmDisabling) => confirmDisabling && this.stepForm.get(propertyName).setValue(false)
        );

        this.stepForm.get(propertyName).setValue(true);
      }
    };
  }

  protected askDisablingLegacyProperty(propertyTitle: string, modalService: ModalService): Promise<boolean> {
    return modalService
      .openCustom(LegacyPropertyDisablingModalComponent, { collapsing: true, data: { propertyTitle } })
      .then(() => true)
      .catch(() => false);
  }

  protected removeError(controls: AbstractControl[], error: string): void {
    controls.forEach((control) => {
      const errors = control.errors || {};

      delete errors[error];

      control.setErrors(Object.keys(errors).length ? errors : null);
    });
  }

  protected showStorageCostImpact(standardStorageName: string, i18nPipe: I18NextPipe, modalService: ModalService): Promise<boolean> {
    return modalService.open(
      {
        header: {
          title: i18nPipe.transform('wizards:syntheticFullCostImpact')
        },
        footer: {
          okButton: { text: i18nPipe.transform('wizards:syntheticFullCostImpactApplySettings') }
        }
      },
      i18nPipe
        .transform('wizards:syntheticFullCostImpactText', {
          storageClass: 'StorageClass'
        })
        .replace('StorageClass', `<strong>${standardStorageName}</strong>`)
    );
  }

  protected recalculateSyntheticFullVisible(
    storageType: StorageType,
    fStorageClass: StorageClass,
    fAzureAccessTierType: AzureAccessTierType,
    syntheticFullField: string
  ): void {
    if (!this.selectedStorageType && this.isUnix) {
      return void this.setSyntheticFullVisible('Hidden', syntheticFullField);
    }

    /**
     * Need to combine string [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]
     */
    let allowSynthetic: boolean = null;
    let storageClass: StorageClass | AzureAccessTierType = null;

    if (storageType === StorageType.Azure) {
      storageClass = fAzureAccessTierType;
    }

    if (storageType === StorageType.AmazonS3) {
      storageClass = fStorageClass;
    }

    if (storageType === StorageType.Minio) {
      allowSynthetic = this.syntheticFullSettingPossible.value;
    }

    if (storageType === StorageType.S3Compatible) {
      allowSynthetic = this.syntheticFullSettingPossible.value;
    }

    const selector = `${storageType}-${storageClass}-${allowSynthetic}-${this.isNBF}`;
    let value = this.syntheticFullVisibleMap.windows[selector] || 'Hidden';

    if (typeof value === 'object') {
      value = value[this.mainService.type] || 'Hidden';
    }

    this.setSyntheticFullVisible(value, syntheticFullField);
  }

  protected setSyntheticFullVisible(value: 'Enabled' | 'Disabled' | 'Hidden', syntheticFullField: string): void {
    const setTrue = () => {
      if (!this.userInteractWithSteps && !this.initialSyntheticFull) {
        return;
      }

      this.stepForm.get(syntheticFullField).setValue(true);
    };

    this.syntheticFullVisible = value;

    if (value === 'Enabled') {
      return void setTrue();
    }

    this.stepForm.get(syntheticFullField).setValue(false);
  }
}
